import type { HomeBuyingCostRuleSet, MiscCostItem } from "./types.js";

// home-buying-cost composer ruleset — FY2025-26 (1 July 2025 – 30 June 2026).
//
// The ONLY constants this module owns: the INDICATIVE misc upfront-cost ranges.
// Every other figure in the assembled picture comes from a child module
// (stamp-duty / lmi / first-home-scheme / fhss) and is cited there. These misc
// ranges have no single statutory source and vary by provider/property, so each
// SHIPS AS A STAMPED-INDICATIVE RANGE — never a precise quote. Presenting one as
// a definite fee is the estimate-as-quote misleading-conduct edge (ACL s18).
//
// Indicative ranges (transcribed from the cited consumer-finance sources,
// fetched 2026-06-23 — re-snapshot each FY; never mutate a shipped file):
//
//   Conveyancing / legal             : ~$1,500 – $3,000   (Moneysmart / consumer)
//   Building + pest inspection       : ~$400   – $700     (consumer guides)
//   Mortgage registration fee (state): NSW ~$171, VIC ~$120, QLD ~$233 (LTO/LRS)
//   Transfer registration fee (state): NSW ~$171, VIC ~$120, QLD ~$233 (LTO/LRS)
//
// The per-state registration/transfer figures are government Land Titles fees
// (small, schedule-set) — encoded as tight indicative ranges around the cited
// fee so a small fee change between snapshots doesn't read as a wrong quote.

const MONEYSMART_UPFRONT =
  "https://moneysmart.gov.au/buying-a-home/mortgages-with-low-or-no-deposit";
const CONSUMER_CONVEYANCING =
  "https://moneysmart.gov.au/buying-a-home"; // conveyancing cost guidance
const NSW_LRS = "https://www.nswlrs.com.au/Fees";
const VIC_LANDATA = "https://www.land.vic.gov.au/land-registration/fees";
const QLD_TITLES = "https://www.titlesqld.com.au/fees/";

/** Common (all-state) indicative misc costs. */
const MISC_COMMON: readonly MiscCostItem[] = [
  {
    id: "conveyancing",
    label: "Conveyancing / legal",
    range: { low: 1_500, high: 3_000 },
    source: CONSUMER_CONVEYANCING,
  },
  {
    id: "building_pest_inspection",
    label: "Building + pest inspection",
    range: { low: 400, high: 700 },
    source: MONEYSMART_UPFRONT,
  },
];

/** State Land Titles registration + transfer fees (indicative ranges). */
const MISC_NSW: readonly MiscCostItem[] = [
  {
    id: "mortgage_registration",
    label: "Mortgage registration fee (NSW LRS)",
    range: { low: 165, high: 175 },
    source: NSW_LRS,
  },
  {
    id: "transfer_registration",
    label: "Transfer registration fee (NSW LRS)",
    range: { low: 165, high: 175 },
    source: NSW_LRS,
  },
];

const MISC_VIC: readonly MiscCostItem[] = [
  {
    id: "mortgage_registration",
    label: "Mortgage registration fee (Land Use Victoria)",
    range: { low: 115, high: 125 },
    source: VIC_LANDATA,
  },
  {
    id: "transfer_registration",
    label: "Transfer registration fee (Land Use Victoria)",
    range: { low: 115, high: 125 },
    source: VIC_LANDATA,
  },
];

const MISC_QLD: readonly MiscCostItem[] = [
  {
    id: "mortgage_registration",
    label: "Mortgage registration fee (Titles Queensland)",
    range: { low: 225, high: 240 },
    source: QLD_TITLES,
  },
  {
    id: "transfer_registration",
    label: "Transfer registration fee (Titles Queensland)",
    range: { low: 225, high: 240 },
    source: QLD_TITLES,
  },
];

export const fy2025_26: HomeBuyingCostRuleSet = {
  engine: "home-buying-cost",
  fy: "2025-26",
  effectiveFrom: "2025-07-01",
  effectiveTo: "2026-06-30",
  sourcesVerifiedAt: "2026-06-23",
  miscCommon: MISC_COMMON,
  miscByState: {
    NSW: MISC_NSW,
    VIC: MISC_VIC,
    QLD: MISC_QLD,
  },
};
